import { ComponentProps, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import { Button, Modal, Subheading, Label } from '@treams/treams-ui';
import WorkspacePlanCard from './components/WorkspacePlanCard';
import { AddIcon } from '@treams/treams-icons';
import { Flex } from 'reflexbox';

const IconButton = styled(Button)`
	padding-left: 5px;
	padding-right: 10px;
`;

const AddButton = (props: Exclude<ComponentProps<typeof Button>, 'icon' | 'small' | 'type'>) => (
	<IconButton {...props} icon={<AddIcon />} type="transparent" small />
);

export default function WorkspaceEditModalPlan(props: {
	workspace: ComponentProps<typeof WorkspacePlanCard>['workspace'] & {
		plans: ComponentProps<typeof WorkspacePlanCard>['plan'][];
	};
}) {
	const { t } = useTranslation();

	const [create] = graphql.useCreateWorkspacePlanMutation({
		update(cache, { data }) {
			const newPlan = data?.createWorkspacePlan.plan;

			if (newPlan) {
				cache.modify({
					id: cache.identify(props.workspace),
					fields: {
						plans(existingPlans = [], { toReference }) {
							return [...existingPlans, toReference(newPlan)];
						},
					},
				});
			}
		},
	});
	const [openID, setOpenID] = useState<string | null>(null);

	const plans = _.sortBy(props.workspace.plans, (plan) => {
		return plan.endDate && new Date(plan.endDate).getTime();
	}).reverse();

	const addPlanOfType = useCallback(
		async (type: graphql.WorkspacePlanType) => {
			await create({
				variables: {
					input: {
						workspaceID: props.workspace.id,
						type,
					},
				},
			});
		},
		[create, props.workspace.id],
	);

	return (
		<Modal.Contents>
			<Subheading mb={2}>{t('settings.workspaces.edit.plans.type.title')}</Subheading>
			{plans.map((plan) => (
				<WorkspacePlanCard
					key={plan.id}
					plan={plan}
					workspace={props.workspace}
					isCollapsed={plan.id !== openID}
					toggleCollapse={() => {
						if (openID === plan.id) {
							setOpenID(null);
						} else {
							setOpenID(plan.id);
						}
					}}
				/>
			))}
			<Label mt={3}>{t('settings.workspaces.startTrial')}</Label>
			<Flex mt={2}>
				{Object.keys(graphql.WorkspacePlanType).map((type) => (
					<AddButton
						key={type}
						onClick={addPlanOfType.bind(null, graphql.WorkspacePlanType[type])}
						title={t(`workspaces.plans.type.${_.camelCase(type)}`)}
						mr={1}
					/>
				))}
			</Flex>
		</Modal.Contents>
	);
}
